(() => {
  'use strict';

  const config = window.KASShopify || { routes: {}, strings: {} };
  const drawerState = { active: null, returnFocus: null, inerted: [] };
  let toastTimer;
  let searchTimer;
  let searchController;

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function formatMoney(cents, format = config.moneyFormat || '${{amount}}') {
    const value = Number(cents || 0);
    const formatWithDelimiters = (amount, precision = 2, thousands = ',', decimal = '.') => {
      const number = (amount / 100).toFixed(precision);
      const parts = number.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    };
    const match = String(format).match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return `$${formatWithDelimiters(value)}`;
    let replacement;
    switch (match[1]) {
      case 'amount_no_decimals': replacement = formatWithDelimiters(value, 0); break;
      case 'amount_with_comma_separator': replacement = formatWithDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': replacement = formatWithDelimiters(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': replacement = formatWithDelimiters(value, 2, "'", '.'); break;
      default: replacement = formatWithDelimiters(value);
    }
    return String(format).replace(match[0], replacement);
  }

  function showToast(message) {
    const toast = document.querySelector('[data-toast]');
    if (!toast || !message) return;
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    toastTimer = window.setTimeout(() => { toast.hidden = true; }, 3200);
  }

  function setOutsideInert(drawer) {
    drawerState.inerted = [];
    let node = drawer;
    while (node && node.parentElement && node.parentElement !== document.documentElement) {
      [...node.parentElement.children].forEach((sibling) => {
        if (sibling === node || sibling.tagName === 'SCRIPT' || sibling.matches?.('[data-toast]')) return;
        drawerState.inerted.push({
          element: sibling,
          inert: sibling.inert,
          ariaHidden: sibling.getAttribute('aria-hidden')
        });
        sibling.inert = true;
        sibling.setAttribute('aria-hidden', 'true');
      });
      node = node.parentElement;
      if (node === document.body) break;
    }
  }

  function restoreOutsideInert() {
    drawerState.inerted.forEach(({ element, inert, ariaHidden }) => {
      element.inert = inert;
      if (ariaHidden === null) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', ariaHidden);
    });
    drawerState.inerted = [];
  }

  function openDrawer(name, trigger) {
    const drawer = document.querySelector(`[data-drawer="${name}"]`);
    if (!drawer) return;
    const returnFocus = drawerState.active ? drawerState.returnFocus : (trigger || document.activeElement);
    if (drawerState.active) closeDrawer(false);
    drawerState.returnFocus = returnFocus;
    drawerState.active = drawer;
    drawer.hidden = false;
    drawer.classList.add('is-open');
    document.body.classList.add('drawer-open');
    setOutsideInert(drawer);
    const panel = drawer.querySelector('[data-drawer-panel]') || drawer;
    window.requestAnimationFrame(() => {
      const preferred = drawer.querySelector('[autofocus], [data-predictive-search-input]');
      const first = preferred || panel.querySelector(focusableSelector) || panel;
      first.focus({ preventScroll: true });
    });
  }

  function closeDrawer(restoreFocus = true) {
    const drawer = drawerState.active;
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.hidden = true;
    restoreOutsideInert();
    document.body.classList.remove('drawer-open');
    drawerState.active = null;
    if (restoreFocus && drawerState.returnFocus instanceof HTMLElement && document.contains(drawerState.returnFocus)) {
      drawerState.returnFocus.focus({ preventScroll: true });
    }
    if (restoreFocus) drawerState.returnFocus = null;
  }

  function trapFocus(event) {
    if (!drawerState.active || event.key !== 'Tab') return;
    const panel = drawerState.active.querySelector('[data-drawer-panel]') || drawerState.active;
    const focusable = [...panel.querySelectorAll(focusableSelector)].filter((item) => item.offsetParent !== null);
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    } else if (!panel.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    }
  }

  function setFormBusy(form, busy) {
    form.setAttribute('aria-busy', String(busy));
    form.querySelectorAll('button[type="submit"]').forEach((button) => { button.disabled = busy; });
  }

  function showFormError(form, message) {
    const error = form.querySelector('[data-form-error]');
    const copy = message || config.strings.cartError || 'Something went wrong. Please try again.';
    if (!error) {
      showToast(copy);
      return;
    }
    error.textContent = copy;
    error.hidden = false;
  }

  async function addProduct(form) {
    setFormBusy(form, true);
    const error = form.querySelector('[data-form-error]');
    if (error) error.hidden = true;
    try {
      const response = await fetch(`${config.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message);
      if (drawerState.active) closeDrawer(false);
      const addedMessage = config.strings.added || 'Added to your snack stash.';
      try {
        await refreshCart();
        openDrawer('cart', drawerState.returnFocus || form.querySelector('button[type="submit"]'));
        showToast(addedMessage);
      } catch {
        showToast(`${addedMessage} Open the cart to review it.`);
      }
    } catch (errorMessage) {
      showFormError(form, errorMessage.message);
    } finally {
      setFormBusy(form, false);
    }
  }

  function cartItemElement(item) {
    const article = document.createElement('article');
    article.className = 'cart-item';
    article.dataset.cartLine = item.key;

    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.product_title || '';
      image.loading = 'lazy';
      article.append(image);
    }

    const copy = document.createElement('div');
    const heading = document.createElement('h3');
    const link = document.createElement('a');
    link.href = item.url;
    link.textContent = item.product_title;
    heading.append(link);
    copy.append(heading);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variant = document.createElement('small');
      variant.textContent = item.variant_title;
      copy.append(variant);
    }

    const price = document.createElement('p');
    price.textContent = formatMoney(item.final_line_price);
    copy.append(price);

    const quantity = document.createElement('div');
    quantity.className = 'quantity-control';
    quantity.setAttribute('aria-label', `Quantity for ${item.product_title}`);
    quantity.append(cartChangeButton(item, Math.max(0, item.quantity - 1), 'minus', 'Decrease quantity'));
    const count = document.createElement('span');
    count.textContent = item.quantity;
    quantity.append(count);
    quantity.append(cartChangeButton(item, item.quantity + 1, 'plus', 'Increase quantity'));
    copy.append(quantity);
    article.append(copy);

    const remove = cartChangeButton(item, 0, 'trash', `Remove ${item.product_title}`);
    remove.className = 'remove-item';
    article.append(remove);
    return article;
  }

  function cartChangeButton(item, quantity, icon, label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.cartChange = '';
    button.dataset.lineKey = item.key;
    button.dataset.quantity = String(quantity);
    button.setAttribute('aria-label', label);
    const glyph = document.createElement('i');
    glyph.className = `fa-solid fa-${icon}`;
    glyph.setAttribute('aria-hidden', 'true');
    button.append(glyph);
    return button;
  }

  function renderCart(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.classList.toggle('is-empty', cart.item_count === 0);
      const trigger = count.closest('[data-drawer-open="cart"]');
      if (trigger) trigger.setAttribute('aria-label', `Open cart with ${cart.item_count} items`);
    });

    const container = document.querySelector('[data-cart-items]');
    const empty = document.querySelector('[data-cart-empty]');
    const footer = document.querySelector('[data-cart-footer]');
    if (!container || !empty || !footer) return;
    container.querySelectorAll('[data-cart-line]').forEach((line) => line.remove());
    cart.items.forEach((item) => container.append(cartItemElement(item)));
    empty.hidden = cart.item_count > 0;
    footer.hidden = cart.item_count === 0;
    const total = footer.querySelector('[data-cart-total]');
    if (total) total.textContent = formatMoney(cart.total_price);
  }

  async function refreshCart() {
    const response = await fetch(`${config.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(config.strings.cartError);
    const cart = await response.json();
    renderCart(cart);
    return cart;
  }

  async function changeCartLine(button) {
    const lineKey = button.dataset.lineKey;
    const focusLabel = button.getAttribute('aria-label');
    button.disabled = true;
    try {
      const response = await fetch(`${config.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: button.dataset.lineKey, quantity: Number(button.dataset.quantity) })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message);
      renderCart(payload);
      const replacement = [...document.querySelectorAll('[data-cart-change]')].find((candidate) => (
        candidate.dataset.lineKey === lineKey && candidate.getAttribute('aria-label') === focusLabel
      ));
      const fallback = drawerState.active?.querySelector('[data-drawer-close], [data-cart-footer] a, [data-cart-footer] button');
      (replacement || fallback)?.focus({ preventScroll: true });
    } catch (error) {
      showToast(error.message || config.strings.cartError);
    } finally {
      button.disabled = false;
    }
  }

  async function loadQuickView(url, trigger) {
    const content = document.querySelector('[data-quick-view-content]');
    const loading = document.querySelector('[data-quick-view-loading]');
    if (!content || !loading) {
      window.location.assign(url);
      return;
    }
    content.replaceChildren();
    loading.hidden = false;
    openDrawer('quick-view', trigger);
    try {
      const separator = url.includes('?') ? '&' : '?';
      const response = await fetch(`${url}${separator}view=quick-view`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Unable to load product.');
      content.innerHTML = await response.text();
      loading.hidden = true;
      const heading = content.querySelector('h2');
      if (heading) heading.focus?.();
    } catch (error) {
      closeDrawer(false);
      window.location.assign(url);
    }
  }

  function updateVariant(select) {
    const scope = select.closest('[data-product-section], .product-modal');
    if (!scope) return;
    const json = scope.querySelector('[data-product-variants]');
    if (!json) return;
    let variants;
    try { variants = JSON.parse(json.textContent); } catch { return; }
    const variant = variants.find((item) => String(item.id) === String(select.value));
    if (!variant) return;
    const price = scope.querySelector('[data-product-price]');
    const comparePrice = scope.querySelector('[data-compare-price]');
    const addButton = scope.querySelector('[data-add-to-cart]');
    const addText = scope.querySelector('[data-add-to-cart-text]');
    if (price) price.textContent = formatMoney(variant.price);
    if (comparePrice) {
      const hasComparePrice = Number(variant.compare_at_price) > Number(variant.price);
      comparePrice.textContent = hasComparePrice ? formatMoney(variant.compare_at_price) : '';
      comparePrice.hidden = !hasComparePrice;
    }
    if (addButton) addButton.disabled = !variant.available;
    if (addText) addText.textContent = variant.available ? config.strings.addToCart : config.strings.soldOut;
    if (variant.featured_image?.src) {
      const image = scope.querySelector('[data-product-main-image], .product-detail__primary-media img');
      if (image) { image.src = variant.featured_image.src; image.removeAttribute('srcset'); }
    }
  }

  function predictivePrice(product) {
    const numeric = Number(product.price);
    if (Number.isFinite(numeric)) return formatMoney(Math.round(numeric * 100));
    return product.price || '';
  }

  function renderPredictiveSearch(products, results) {
    results.replaceChildren();
    if (products.length === 0) {
      const empty = document.createElement('p');
      empty.textContent = config.strings.searchEmpty || 'No treats matched that search yet.';
      results.append(empty);
      return;
    }
    products.forEach((product) => {
      const link = document.createElement('a');
      link.href = product.url;
      link.dataset.quickViewUrl = product.url;
      if (product.featured_image?.url) {
        const image = document.createElement('img');
        image.src = product.featured_image.url;
        image.alt = '';
        image.loading = 'lazy';
        link.append(image);
      }
      const copy = document.createElement('span');
      const title = document.createElement('strong');
      title.textContent = product.title;
      const price = document.createElement('small');
      price.textContent = predictivePrice(product);
      copy.append(title, price);
      link.append(copy);
      const arrow = document.createElement('i');
      arrow.className = 'fa-solid fa-arrow-right';
      arrow.setAttribute('aria-hidden', 'true');
      link.append(arrow);
      results.append(link);
    });
  }

  async function runPredictiveSearch(input) {
    const results = document.querySelector('[data-predictive-search-results]');
    if (!results) return;
    if (searchController) {
      searchController.abort();
      searchController = null;
    }
    const query = input.value.trim();
    if (query.length < 2) {
      results.replaceChildren();
      return;
    }
    const controller = new AbortController();
    searchController = controller;
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last'
    });
    try {
      const response = await fetch(`${config.routes.predictiveSearch}.json?${params}`, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Search unavailable');
      const payload = await response.json();
      if (input.value.trim() !== query) return;
      renderPredictiveSearch(payload.resources?.results?.products || [], results);
    } catch (error) {
      if (error.name !== 'AbortError') renderPredictiveSearch([], results);
    } finally {
      if (searchController === controller) searchController = null;
    }
  }

  async function loadMore(link) {
    if (link.getAttribute('aria-busy') === 'true') return;
    link.setAttribute('aria-busy', 'true');
    const original = link.textContent;
    link.textContent = 'Loading flavor…';
    try {
      const response = await fetch(link.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Unable to load more products.');
      const documentNext = new DOMParser().parseFromString(await response.text(), 'text/html');
      const currentGrid = document.querySelector('[data-product-grid]');
      const nextGrid = documentNext.querySelector('[data-product-grid]');
      if (!currentGrid || !nextGrid) throw new Error('Product grid not found.');
      [...nextGrid.children].forEach((card) => currentGrid.append(card));
      const nextLink = documentNext.querySelector('[data-load-more]');
      const nextCount = documentNext.querySelector('.result-count');
      const currentCount = document.querySelector('.result-count');
      if (nextCount && currentCount) currentCount.textContent = nextCount.textContent;
      if (nextLink) {
        link.href = nextLink.href;
        link.textContent = original;
        link.removeAttribute('aria-busy');
      } else {
        link.remove();
      }
    } catch (error) {
      link.textContent = original;
      link.removeAttribute('aria-busy');
      showToast(error.message);
    }
  }

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-drawer-open]');
    if (open) {
      event.preventDefault();
      openDrawer(open.dataset.drawerOpen, open);
      if (open.dataset.drawerOpen === 'cart') refreshCart().catch(() => {});
      return;
    }

    if (event.target.closest('[data-drawer-close]') || (drawerState.active && event.target === drawerState.active)) {
      event.preventDefault();
      closeDrawer(true);
      return;
    }

    const quickView = event.target.closest('[data-quick-view-url]');
    if (quickView && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
      event.preventDefault();
      loadQuickView(quickView.dataset.quickViewUrl, quickView);
      return;
    }

    const change = event.target.closest('[data-cart-change]');
    if (change) {
      event.preventDefault();
      changeCartLine(change);
      return;
    }

    const minus = event.target.closest('[data-quantity-minus]');
    const plus = event.target.closest('[data-quantity-plus]');
    if (minus || plus) {
      const control = event.target.closest('[data-quantity-control]');
      const input = control?.querySelector('input[type="number"]');
      if (!input) return;
      const next = Number(input.value || 1) + (plus ? 1 : -1);
      input.value = String(Math.max(Number(input.min || 1), next));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const media = event.target.closest('[data-media-thumbnail]');
    if (media) {
      const image = document.querySelector('.product-detail__primary-media img');
      if (image && media.dataset.mediaSrc) {
        image.src = media.dataset.mediaSrc;
        image.removeAttribute('srcset');
        image.alt = media.querySelector('img')?.alt || image.alt;
      }
      return;
    }

    const more = event.target.closest('[data-load-more]');
    if (more) {
      event.preventDefault();
      loadMore(more);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-ajax-product-form]');
    if (!form || !window.fetch) return;
    event.preventDefault();
    addProduct(form);
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-variant-select]')) updateVariant(event.target);
    if (event.target.matches('[data-sort-select]')) event.target.form?.requestSubmit();
  });

  document.addEventListener('input', (event) => {
    if (!event.target.matches('[data-predictive-search-input]')) return;
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => runPredictiveSearch(event.target), 180);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && drawerState.active) {
      event.preventDefault();
      closeDrawer(true);
      return;
    }
    trapFocus(event);
  });

  document.addEventListener('click', (event) => {
    if (drawerState.active?.dataset.drawer === 'mobile-menu' && event.target.closest('a')) closeDrawer(false);
  });
})();
